import React, { useEffect, useMemo, useRef } from 'react';
import Map, { Layer, MapRef, Source } from 'react-map-gl';
import { LngLatBounds } from 'mapbox-gl';
import { getAreaCenter, parseAreaGeoJson } from '../utils/areaGeoJson';
import { getMapStyle } from '../utils/mapboxStyle';
import { MAPBOX_TOKEN } from '../utils/mapboxToken';
import { downloadDiscussionGraphic } from '../utils/discussionGraphic';

interface Props {
  title: string;
  validUntil?: string | null;
  concerning?: string | null;
  tornadoProbability?: string | null;
  tornadoPeakMph?: string | null;
  windProbability?: string | null;
  hailProbability?: string | null;
  areaGeojson?: string | null;
  highlightColor?: string | null;
  windPeakMph?: string | null;
  hailPeakIn?: string | null;
  mdNumber?: number | null;
  issuedAt?: string | null;
  validFrom?: string | null;
  issuedBy?: string | null;
  areasAffected?: string | null;
  summary?: string | null;
}

const MesoscaleGraphic: React.FC<Props> = ({
  title,
  validUntil,
  concerning,
  tornadoProbability,
  windProbability,
  hailProbability,
  areaGeojson,
  highlightColor,
  tornadoPeakMph,
  windPeakMph,
  hailPeakIn,
  mdNumber,
  issuedAt,
  validFrom,
  issuedBy,
  areasAffected,
  summary,
}) => {
  const mapRef = useRef<MapRef | null>(null);

  const parsedArea = useMemo(() => {
    if (!areaGeojson) return null;
    try {
      return parseAreaGeoJson(areaGeojson);
    } catch {
      return null;
    }
  }, [areaGeojson]);

  const areaPositions = useMemo(() => {
    if (!parsedArea || parsedArea.length < 3) return [] as [number, number][];
    return parsedArea.map(([lng, lat]) => [lat, lng] as [number, number]);
  }, [parsedArea]);

  const polygonCoordinates = useMemo(() => {
    if (!parsedArea || parsedArea.length < 3) return null;
    return [...parsedArea, parsedArea[0]];
  }, [parsedArea]);

  const center = useMemo(() => {
    if (parsedArea && parsedArea.length > 0) {
      return getAreaCenter(parsedArea);
    }
    return [43.2, -84.5] as [number, number];
  }, [parsedArea]);

  const hasArea = areaPositions.length >= 3;
  const normalizedHighlightColor = highlightColor ?? '#2d7ff9';

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || areaPositions.length === 0) return;

    const bounds = areaPositions.reduce((accumulator, [lat, lng]) => {
      accumulator.extend([lng, lat]);
      return accumulator;
    }, new LngLatBounds([areaPositions[0][1], areaPositions[0][0]], [areaPositions[0][1], areaPositions[0][0]]));

    map.fitBounds(bounds, { padding: 24, maxZoom: 10, duration: 0 });
  }, [areaPositions]);
  
  // Get current theme to adjust colors for light/dark modes
  const isLightMode = document.body.dataset.theme === 'light';
  const tornadoBg = isLightMode ? 'rgba(30,108,217,0.08)' : 'rgba(255,255,255,0.03)';
  const windBg = isLightMode ? 'rgba(247,144,49,0.12)' : 'rgba(247,144,49,0.06)';
  const hailBg = isLightMode ? 'rgba(255,178,89,0.12)' : 'rgba(255,178,89,0.06)';

  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'stretch', flexWrap: 'wrap' }}>
      <div style={{ width: 220, minWidth: 220, background: 'var(--panel-strong)', border: '1px solid var(--line)', borderRadius: 8, padding: 12 }}>
        <h3 style={{ margin: 0, fontSize: '1.05rem', color: 'var(--ink)' }}>{title}</h3>
        <button
          type="button"
          className="btn btn-secondary"
          style={{ marginTop: 10, width: '100%' }}
          onClick={() => void downloadDiscussionGraphic({ title, validUntil, validFrom, issuedAt, issuedBy, mdNumber, areasAffected, concerning, tornadoProbability, windProbability, hailProbability, areaGeojson, highlightColor, summary })}
        >
          Download PNG
        </button>
        {validUntil && <p style={{ margin: '6px 0', color: 'var(--muted)', fontSize: '0.85rem' }}>Valid until: {new Date(validUntil).toLocaleString()}</p>}
        {concerning && <p style={{ margin: '6px 0', color: 'var(--muted)', fontSize: '0.85rem' }}>Concerning: {concerning}</p>}

        <div style={{ marginTop: 8 }}>
          <p style={{ margin: '6px 0', color: 'var(--muted)', fontWeight: 700 }}>Most Probable Peak Intensity (wind mph / hail in)</p>
          <div style={{ display: 'grid', gap: 8 }}>
            {tornadoPeakMph ? (
              <div style={{ padding: 8, borderRadius: 8, background: tornadoBg, color: 'var(--ink)', fontWeight: 700 }}>Tornado peak: {tornadoPeakMph} mph</div>
            ) : tornadoProbability ? (
              <div style={{ padding: 8, borderRadius: 8, background: tornadoBg, color: 'var(--ink)', fontWeight: 700 }}>Tornado: {tornadoProbability}</div>
            ) : null}
            {windPeakMph ? (
              <div style={{ padding: 8, borderRadius: 8, background: windBg, color: 'var(--ink)', fontWeight: 700 }}>Wind peak: {windPeakMph} mph</div>
            ) : windProbability ? (
              <div style={{ padding: 8, borderRadius: 8, background: windBg, color: 'var(--ink)', fontWeight: 700 }}>Wind: {windProbability}</div>
            ) : null}
            {hailPeakIn ? (
              <div style={{ padding: 8, borderRadius: 8, background: hailBg, color: 'var(--ink)', fontWeight: 700 }}>Hail peak: {hailPeakIn} in</div>
            ) : hailProbability ? (
              <div style={{ padding: 8, borderRadius: 8, background: hailBg, color: 'var(--ink)', fontWeight: 700 }}>Hail: {hailProbability}</div>
            ) : null}
          </div>
        </div>
      </div>

      <div style={{ flex: 1, minWidth: 320, borderRadius: 8, overflow: 'hidden', border: '1px solid var(--line)' }}>
        <Map
          key={document.body.dataset.theme === 'light' ? 'light-map' : 'dark-map'}
          ref={mapRef}
          initialViewState={{ latitude: center[0], longitude: center[1], zoom: 6 }}
          mapStyle={getMapStyle()}
          mapboxAccessToken={MAPBOX_TOKEN}
          style={{ height: 360, width: '100%' }}
          scrollZoom={false}
          reuseMaps
        >
          {hasArea && polygonCoordinates && (
            <Source
              id="mesoscale-polygon"
              type="geojson"
              data={{
                type: 'Feature',
                geometry: {
                  type: 'Polygon',
                  coordinates: [polygonCoordinates],
                },
                properties: {},
              }}
            >
              <Layer
                id="mesoscale-polygon-fill"
                type="fill"
                paint={{
                  'fill-color': normalizedHighlightColor,
                  'fill-opacity': 0.24,
                }}
              />
              <Layer
                id="mesoscale-polygon-line"
                type="line"
                paint={{
                  'line-color': normalizedHighlightColor,
                  'line-width': 2,
                }}
              />
            </Source>
          )}
        </Map>
      </div>
    </div>
  );
};

export default MesoscaleGraphic;
